<template>
  <div class="column">
    <input-dropdown
      v-slot="{ item: yAxisValue }"
      class="w-full"
      :label="$t('labels.yAxis')"
      :options="yAxisOptions"
      :value="axes.yAxis"
      @update:value="emit('update:y-axis', $event)"
    >
      {{
        te(`chartAxes.${yAxisValue}`)
          ? $t(`chartAxes.${yAxisValue}`)
          : yAxisValue
      }}
    </input-dropdown>
    <input-dropdown
      v-slot="{ item: xAxisValue }"
      class="w-full"
      :label="$t('labels.xAxis')"
      :options="xAxisOptions"
      :value="axes.xAxis"
      @update:value="emit('update:x-axis', $event)"
    >
      {{
        te(`chartAxes.${xAxisValue}`)
          ? $t(`chartAxes.${xAxisValue}`)
          : xAxisValue
      }}
    </input-dropdown>

    <template v-if="axes.xAxis == 'target' && orderedTargets.length == 0">
      <p class="message-error mt-0">
        <lucide-icon name="triangle-alert" /> {{ $t("errors.noTargets") }}
      </p>
    </template>
    <template v-else-if="axes.xAxis == 'resource' && orderedResources.length == 0">
      <p class="message-error mt-0">
        <lucide-icon name="triangle-alert" /> {{ $t("errors.noResources") }}
      </p>
    </template>
    <template v-else-if="axes.xAxis == 'index' && orderedIndices.length == 0">
      <p class="message-error mt-0">
        <lucide-icon name="triangle-alert" /> {{ $t("errors.noIndices") }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import InputDropdown from "@core/input/InputDropdown.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

import { useFeatures } from "@editor/store/features.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";

import { charts } from "@editor/items/charts/charts";

import type { ChartType } from "@editor/items/charts/ChartItem";

const props = defineProps<{
  chartType: ChartType | null;
  axes: { xAxis: string; yAxis: string };
}>();
const emit = defineEmits<{
  (e: "update:x-axis", value: string): void;
  (e: "update:y-axis", value: string): void;
}>();

const features = useFeatures();
const { te } = useI18n();
const orderedTargets = computed(() => useTargetStore().orderedItems);
const orderedResources = computed(() => useResourceStore().orderedItems);
const orderedIndices = computed(() => useIndexStore().orderedItems);

const chartConfig = computed(() => {
  if (!props.chartType) return null;
  return charts[props.chartType] || null;
});

const xAxisOptions = computed(() => {
  if (!chartConfig.value) return [];
  const s = new Set(chartConfig.value.axisOptions.map((o) => o?.x));

  // Feature-filter out any deactivated types
  if (!features.target) s.delete("target");
  if (!features.resource) s.delete("resource");
  if (!features.index) s.delete("index");

  return Array.from(s).filter((i) => i != null);
});

const yAxisOptions = computed(() => {
  if (!chartConfig.value) return [];
  const s = new Set(chartConfig.value.axisOptions.map((o) => o?.y));
  return Array.from(s).filter((i) => i != null);
});
</script>

<style></style>
